import {once} from 'lodash';
import React, {
  createContext,
  type RefObject,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';

import {
  recordRumAction,
  recordRumDurationVital,
} from '../../util/datadog/datadogRum';
import {
  type TileMedia,
  type TileMediaSpec,
  type TileMediaTracking,
} from '../MediaCard';
import {
  defaultState as defaultPanelState,
  type MediaBrowserPanelTrackingContextState,
  useMediaBrowserPanelTrackingContext,
  useMediaBrowserPanelTrackingContextMaybe,
} from './MediaBrowserPanelTrackingContext';
import {resolveTileMediaCacheHit} from './utils/isCacheHit';
import {
  calculateTotalDuration,
  calculateTrackingStages,
} from './utils/tracking';

// Legacy metric name - kept for backward compatibility
const DD_RUM_METRIC_LEGACY = 'mediaBrowserPanelTileTracking';
const DD_RUM_DESCRIPTION = 'Media browser panel tile load duration';

// New per-stage metric names
const DD_RUM_METRIC_STAGE_PREFIX = 'mediaBrowserPanelTile';
const DD_RUM_METRIC_TOTAL = 'mediaBrowserPanelTile.total';

// Feature flag to enable new per-stage vitals (set to true to enable new tracking)
// TODO: Remove legacy tracking once new metrics are validated
const ENABLE_PER_STAGE_VITALS = true;

const EmptyTileMedia: TileMedia = {
  mediaKey: '',
  step: 0,
  index: 0,
  historyRow: {},
  blob: new Blob(),
  isNullBlob: true,
  objectURL: '',
  filePath: '',
  directURL: '',
  mediaSliderKey: '',
  mediaSliderKeyValue: 0,
};

type MediaBrowserPanelTileTrackingContextType = {
  state: RefObject<MediaBrowserPanelTileTrackingContextState>;
  setAvailableFilesReceivedPerformanceMs: (value: number) => void;
  setDirectUrlReceivedPerformanceMs: (value: number) => void;
  setBlobFetchCompletePerformanceMs: (value: number) => void;
  setDataCachedPerformanceMs: (value: number) => void;
  setStatusObservedPerformanceMs: (value: number) => void;
  setMediaLoadedPerformanceMs: (value: number) => void;
  setMediaLoadedSuccess: (value: boolean) => void;
  setTileMedia: (value: TileMedia) => void;
  trackSuccess: (
    tileMedia: TileMedia | null,
    tileMediaSpec: TileMediaSpec | undefined,
    getMediaTracking?: (
      tileMediaSpec: TileMediaSpec
    ) => TileMediaTracking | null,
    metadata?: SuccessMetadata
  ) => void;
  trackError: (
    tileMedia: TileMedia | null,
    tileMediaSpec: TileMediaSpec | undefined,
    getMediaTracking?: (
      tileMediaSpec: TileMediaSpec
    ) => TileMediaTracking | null
  ) => void;
  emit: () => void;
  isMediaBrowserPanelTrackingSupported: boolean;
};

export type MediaBrowserPanelTileTrackingContextState = {
  runName: string;
  mediaKey: string;
  /** From {@link TileMediaSpec.runState} when the tile load completes. */
  runState?: TileMediaSpec['runState'];
  componentCreatedMs: number;
  componentCreatedPerformanceMs: number;
  availableFilesReceivedPerformanceMs: number;
  directUrlReceivedPerformanceMs: number;
  blobFetchCompletePerformanceMs: number;
  dataCachedPerformanceMs: number;
  statusObservedPerformanceMs: number;
  mediaLoadedPerformanceMs: number;
  mediaLoadedSuccess: boolean;
  // Unified query tracking (Project 2)
  thumbnailsRequested: boolean; // was $includeThumbnails: true in the query?
  hlsSrcRequested: boolean; // was $includeHlsSrc: true in the query?
  thumbnailsReceived: boolean; // did the response contain thumbnails?
  hlsSrcReceived: boolean; // did the response contain hlsSrc?
  /** Whether this tile load was served from cache (Apollo or in-memory). */
  isCacheHit: boolean;
  panel: MediaBrowserPanelTrackingContextState;
  media: {
    dimensions: {
      width: number;
      height: number;
    };
    index: number;
    mime: string;
    path: string;
    size: number;
    step: number;
    stepKey: string;
    stepKeyValue: number;
    type: string;
  };
};

export const MediaBrowserPanelTileTrackingContext =
  createContext<MediaBrowserPanelTileTrackingContextType | null>(null);

export const useMediaBrowserPanelTileTrackingContext = () => {
  const context = useContext(MediaBrowserPanelTileTrackingContext);
  if (!context) {
    throw new Error(
      'useMediaBrowserPanelTileTrackingContext must be used within a MediaBrowserPanelTileTrackingContextProvider'
    );
  }
  return context;
};

export const useMediaBrowserPanelTileTrackingContextMaybe: () => MediaBrowserPanelTileTrackingContextType =
  () => {
    const context = useContext(MediaBrowserPanelTileTrackingContext);
    if (!context) {
      // Fallback object with no-op functions and a dummy ref
      return {
        state: {
          current: {
            runName: '',
            mediaKey: '',
            runState: undefined,
            componentCreatedMs: 0,
            componentCreatedPerformanceMs: 0,
            availableFilesReceivedPerformanceMs: 0,
            directUrlReceivedPerformanceMs: 0,
            blobFetchCompletePerformanceMs: 0,
            dataCachedPerformanceMs: 0,
            statusObservedPerformanceMs: 0,
            mediaLoadedPerformanceMs: 0,
            mediaLoadedSuccess: false,
            thumbnailsRequested: false,
            hlsSrcRequested: false,
            thumbnailsReceived: false,
            hlsSrcReceived: false,
            isCacheHit: false,
            panel: defaultPanelState,
            media: {
              dimensions: {width: 0, height: 0},
              index: 0,
              mime: '',
              path: '',
              size: 0,
              step: 0,
              stepKey: '',
              stepKeyValue: 0,
              type: '',
            },
          },
        },
        setAvailableFilesReceivedPerformanceMs: () => {},
        setDirectUrlReceivedPerformanceMs: () => {},
        setBlobFetchCompletePerformanceMs: () => {},
        setDataCachedPerformanceMs: () => {},
        setStatusObservedPerformanceMs: () => {},
        setMediaLoadedPerformanceMs: () => {},
        setMediaLoadedSuccess: () => {},
        setTileMedia: () => {},
        trackSuccess: () => {},
        trackError: () => {},
        emit: () => {},
        isMediaBrowserPanelTrackingSupported: false,
      };
    }
    return context;
  };

export type MediaBrowserPanelTileTrackingContextProviderProps = {
  children: React.ReactNode;
  mediaKey: string;
  panelId: string;
  runName: string;
  disableVideoStream: boolean;
};

const buildMediaFromTile = (tile: TileMedia, mediaKey: string) => {
  return {
    dimensions: {
      width: tile.historyRow?.[mediaKey]?.width ?? 0,
      height: tile.historyRow?.[mediaKey]?.height ?? 0,
    },
    index: tile.index ?? 0,
    mime: tile.historyRow?.[mediaKey]?.format ?? '',
    path: tile.filePath ?? '',
    size: tile.historyRow?.[mediaKey]?.size ?? 0,
    step: tile.step ?? 0,
    stepKey: tile.mediaSliderKey ?? '',
    stepKeyValue: tile.mediaSliderKeyValue ?? 0,
    type: tile.historyRow?.[mediaKey]?._type ?? '',
  };
};

export const MediaBrowserPanelTileTrackingContextProvider = React.memo(
  ({
    children,
    panelId,
    mediaKey,
    runName,
    disableVideoStream,
  }: MediaBrowserPanelTileTrackingContextProviderProps) => {
    const {
      isMediaBrowserPanelTrackingEnabled,
      isMediaBrowserPanelTrackingDebugEnabled,
      isMediaBrowserPanelTrackingSupported,
      state: panelState,
      panelBuildMetricsRef,
    } = useMediaBrowserPanelTrackingContext();

    // Combine all mutable state into a single ref
    const stateRef = useRef<MediaBrowserPanelTileTrackingContextState>({
      runName,
      mediaKey,
      // oxlint-disable-next-line react/purity
      componentCreatedPerformanceMs: performance.now(),
      // oxlint-disable-next-line react/purity
      componentCreatedMs: Date.now(),
      availableFilesReceivedPerformanceMs: 0,
      directUrlReceivedPerformanceMs: 0,
      blobFetchCompletePerformanceMs: 0,
      dataCachedPerformanceMs: 0,
      statusObservedPerformanceMs: 0,
      mediaLoadedPerformanceMs: 0,
      mediaLoadedSuccess: false,
      thumbnailsRequested: false,
      hlsSrcRequested: false,
      thumbnailsReceived: false,
      hlsSrcReceived: false,
      isCacheHit: false,
      panel: {
        ...defaultPanelState,
        ...panelState,
        // oxlint-disable-next-line react/purity
        startTimeMs: panelState.startTimeMs || Date.now(),
      },
      media: buildMediaFromTile(EmptyTileMedia, mediaKey),
    });

    const loggingPrefix = useRef(
      `MediaBrowserPanelTracking ${panelId} tile ${mediaKey}`
    );

    const log = useCallback(
      (event: string, msg: any) => {
        if (isMediaBrowserPanelTrackingDebugEnabled) {
          console.log(loggingPrefix.current, event, JSON.stringify(msg));
        }
      },
      [isMediaBrowserPanelTrackingDebugEnabled]
    );

    const getDuration = useCallback(() => {
      return calculateTotalDuration(stateRef.current);
    }, []);

    const getVital = useCallback(
      (metadata?: SuccessMetadata) => {
        // ensure updated panel state prior to building vital
        if (!stateRef.current) {
          return null;
        }
        stateRef.current.panel = panelState;
        const duration = getDuration();
        if (duration === null) {
          return null;
        }

        const stages = calculateTrackingStages(
          stateRef.current,
          panelBuildMetricsRef
        );
        if (!stages) {
          return null;
        }

        const ddContext = {
          ...stateRef.current,
          stages,
          ...metadata,
        };

        return {
          context: ddContext,
          description: DD_RUM_DESCRIPTION,
          duration,
          startTime: stateRef.current.componentCreatedMs,
        };
      },
      [getDuration, panelBuildMetricsRef, panelState]
    );

    /**
     * Emits legacy single vital with all stages in context.
     * TODO: Remove this function once new per-stage vitals are validated.
     */
    const emitLegacyVital = useCallback((ddContext: any) => {
      recordRumDurationVital(DD_RUM_METRIC_LEGACY, ddContext);
    }, []);

    /**
     * Emits separate vitals for each stage plus a total vital.
     * This provides better queryability and analysis in Datadog.
     *
     * Each stage gets its own vital with correlation context, allowing:
     * - Independent P50/P90/P99 analysis per stage
     * - Stage-specific monitoring and alerts
     * - Filtering and querying by individual stage performance
     */
    const emitPerStageVitals = useCallback(
      (ddContext: any, metadata?: SuccessMetadata) => {
        const stages = ddContext.context?.stages;
        if (!stages) {
          return;
        }

        // Base context shared across all vitals for correlation
        const baseContext = {
          panelLoadUUID: stateRef.current.panel.panelLoadUUID,
          panelID: stateRef.current.panel.panelID,
          mediaType: stateRef.current.panel.mediaType,
          runName: stateRef.current.runName,
          runState: stateRef.current.runState,
          mediaKey: stateRef.current.mediaKey,
          success: stateRef.current.mediaLoadedSuccess,
          media: stateRef.current.media,
          thumbnailsRequested: stateRef.current.thumbnailsRequested,
          hlsSrcRequested: stateRef.current.hlsSrcRequested,
          thumbnailsReceived: stateRef.current.thumbnailsReceived,
          hlsSrcReceived: stateRef.current.hlsSrcReceived,
          isCacheHit: stateRef.current.isCacheHit,
          reactFetchEnabled: stateRef.current.panel.reactFetchEnabled,
          activeTileCount: stateRef.current.panel.activeTileCount,
          layoutTileCount: stateRef.current.panel.layoutTileCount,
          panelMode: stateRef.current.panel.panelMode,
          pageCap: stateRef.current.panel.pageCap,
          loadTier: stateRef.current.panel.loadTier,
          tileCountBucket: stateRef.current.panel.tileCountBucket,
          ...metadata,
        };

        // Emit a separate vital for each stage
        Object.entries(stages).forEach(([stageName, stage]: [string, any]) => {
          recordRumDurationVital(`${DD_RUM_METRIC_STAGE_PREFIX}.${stageName}`, {
            startTime: stateRef.current.componentCreatedMs + stage.startTime,
            duration: stage.duration,
            description: `Media tile ${stageName} stage duration`,
            context: {
              ...baseContext,
              stageName,
              stageStartTime: stage.startTime,
            },
          });
        });

        // Also emit total duration for convenience
        recordRumDurationVital(DD_RUM_METRIC_TOTAL, {
          startTime: ddContext.startTime,
          duration: ddContext.duration,
          description: DD_RUM_DESCRIPTION,
          context: {
            ...baseContext,
            stages, // Keep full stage breakdown for reference
          },
        });
      },
      []
    );

    const emit = useCallback(
      (metadata?: SuccessMetadata) => {
        if (!isMediaBrowserPanelTrackingEnabled) {
          return;
        }

        const ddContext = getVital(metadata);
        // Only emit if we have a valid vital with duration
        if (!ddContext) {
          if (isMediaBrowserPanelTrackingDebugEnabled) {
            log('emit_skipped', 'No valid duration available');
          }
          return;
        }

        try {
          // ============================================================
          // LEGACY TRACKING - TODO: Remove after validating new metrics
          // ============================================================
          emitLegacyVital(ddContext);

          // ============================================================
          // NEW PER-STAGE TRACKING
          // ============================================================
          if (ENABLE_PER_STAGE_VITALS) {
            emitPerStageVitals(ddContext, metadata);
          }
        } catch (error) {
          console.error('Error adding duration vital', error);
        } finally {
          if (isMediaBrowserPanelTrackingDebugEnabled) {
            log('emit', ddContext);
          }
        }
      },
      [
        isMediaBrowserPanelTrackingEnabled,
        isMediaBrowserPanelTrackingDebugEnabled,
        log,
        getVital,
        emitLegacyVital,
        emitPerStageVitals,
      ]
    );

    const setAvailableFilesReceivedPerformanceMs = useCallback(
      (value: number) => {
        stateRef.current.availableFilesReceivedPerformanceMs = value;
      },
      []
    );

    const setDirectUrlReceivedPerformanceMs = useCallback((value: number) => {
      stateRef.current.directUrlReceivedPerformanceMs = value;
    }, []);

    const setBlobFetchCompletePerformanceMs = useCallback((value: number) => {
      stateRef.current.blobFetchCompletePerformanceMs = value;
    }, []);

    const setDataCachedPerformanceMs = useCallback((value: number) => {
      stateRef.current.dataCachedPerformanceMs = value;
    }, []);

    const setStatusObservedPerformanceMs = useCallback((value: number) => {
      stateRef.current.statusObservedPerformanceMs = value;
    }, []);

    const setMediaLoadedPerformanceMs = useCallback((value: number) => {
      stateRef.current.mediaLoadedPerformanceMs = value;
    }, []);

    const setMediaLoadedSuccess = useCallback(
      (value: boolean, metadata?: SuccessMetadata) => {
        stateRef.current.mediaLoadedSuccess = value;
        emit(metadata);
      },
      [emit]
    );

    const setTileMedia = useCallback(
      (value: TileMedia) => {
        stateRef.current.media = buildMediaFromTile(value, mediaKey);
      },
      [mediaKey]
    );
    const noOp = useCallback(() => {}, []);

    const tracking = useMediaBrowserPanelTrackingContextMaybe();
    const {incrementCompleted} = tracking;

    const trackCompleted = useCallback(
      (
        success: boolean,
        tileMedia: TileMedia | null,
        tileMediaSpec: TileMediaSpec | undefined,
        getMediaTracking?: (
          tileMediaSpec: TileMediaSpec
        ) => TileMediaTracking | null,
        metadata?: SuccessMetadata
      ) => {
        if (!isMediaBrowserPanelTrackingEnabled || tileMedia === null) {
          return;
        }

        setTileMedia(tileMedia);

        if (tileMediaSpec) {
          stateRef.current.runState = tileMediaSpec.runState;
        }

        // ALWAYS increment panel completion (needed for panel-level tracking)
        // Panel tracks when ALL tiles complete (fresh fetch or cache hit)
        incrementCompleted();

        const tracking = tileMediaSpec
          ? getMediaTracking?.(tileMediaSpec)
          : null;

        if (tracking && tileMediaSpec) {
          const {
            availableFilesReceivedPerformanceMs,
            directUrlReceivedPerformanceMs,
            blobFetchCompletePerformanceMs,
            dataCachedPerformanceMs,
            thumbnailsRequested,
            hlsSrcRequested,
          } = tracking;

          // Set query and response tracking before any early returns so cache hit
          // actions and fresh fetch vitals both include this context.
          stateRef.current.thumbnailsRequested = thumbnailsRequested;
          stateRef.current.hlsSrcRequested = hlsSrcRequested;
          stateRef.current.thumbnailsReceived =
            (tileMedia.thumbnails?.length ?? 0) > 0;
          stateRef.current.hlsSrcReceived = tileMedia.hlsSrc != null;

          const componentCreated =
            stateRef.current.componentCreatedPerformanceMs;
          const isCacheHit = resolveTileMediaCacheHit({
            tracking,
            tileMedia,
            tileMediaSpec,
            componentCreatedPerformanceMs: componentCreated,
            disableVideoStream,
          });
          stateRef.current.isCacheHit = isCacheHit;

          if (isMediaBrowserPanelTrackingDebugEnabled) {
            log('trackCompleted', {
              isCacheHit,
              success,
              componentCreated,
              tracking,
              ...metadata,
            });
          }

          if (isCacheHit) {
            if (isMediaBrowserPanelTrackingDebugEnabled) {
              log('cache_hit_skipped', {
                state: stateRef.current,
                cachedTimestamps: tracking,
              });
            }

            // Backwards compat: keep the action while vitals now carry isCacheHit.
            try {
              recordRumAction('mediaBrowserPanelTile.cacheHit', {
                panelLoadUUID: stateRef.current.panel.panelLoadUUID,
                panelID: stateRef.current.panel.panelID,
                mediaType: stateRef.current.panel.mediaType,
                runName: stateRef.current.runName,
                runState: stateRef.current.runState,
                mediaKey: stateRef.current.mediaKey,
                success,
                media: stateRef.current.media,
                thumbnailsRequested: stateRef.current.thumbnailsRequested,
                hlsSrcRequested: stateRef.current.hlsSrcRequested,
                thumbnailsReceived: stateRef.current.thumbnailsReceived,
                hlsSrcReceived: stateRef.current.hlsSrcReceived,
                isCacheHit: true,
                reactFetchEnabled: stateRef.current.panel.reactFetchEnabled,
                activeTileCount: stateRef.current.panel.activeTileCount,
                layoutTileCount: stateRef.current.panel.layoutTileCount,
                panelMode: stateRef.current.panel.panelMode,
                pageCap: stateRef.current.panel.pageCap,
                loadTier: stateRef.current.panel.loadTier,
                tileCountBucket: stateRef.current.panel.tileCountBucket,
              });
            } catch (error) {
              console.error('Error tracking cache hit', error);
            }
          }

          // Fresh fetch and cache hits both emit duration vitals.
          // Inapplicable stages (stale pipeline stamps, pollWait on cache
          // hits) are omitted in calculateTrackingStages — not zeroed.
          setAvailableFilesReceivedPerformanceMs(
            availableFilesReceivedPerformanceMs
          );
          setDirectUrlReceivedPerformanceMs(directUrlReceivedPerformanceMs);
          setBlobFetchCompletePerformanceMs(blobFetchCompletePerformanceMs);
          setDataCachedPerformanceMs(dataCachedPerformanceMs);
        }

        setMediaLoadedPerformanceMs(performance.now());
        setMediaLoadedSuccess(success, metadata);
      },
      [
        setTileMedia,
        incrementCompleted,
        isMediaBrowserPanelTrackingEnabled,
        isMediaBrowserPanelTrackingDebugEnabled,
        log,
        setAvailableFilesReceivedPerformanceMs,
        setDirectUrlReceivedPerformanceMs,
        setBlobFetchCompletePerformanceMs,
        setDataCachedPerformanceMs,
        setMediaLoadedPerformanceMs,
        setMediaLoadedSuccess,
        disableVideoStream,
      ]
    );
    const trackSuccess = useCallback(
      (
        tileMedia: TileMedia | null,
        tileMediaSpec: TileMediaSpec | undefined,
        getMediaTracking?: (
          tileMediaSpec: TileMediaSpec
        ) => TileMediaTracking | null,
        metadata?: SuccessMetadata
      ) =>
        trackCompleted(
          true,
          tileMedia,
          tileMediaSpec,
          getMediaTracking,
          metadata
        ),
      [trackCompleted]
    );
    const trackError = useCallback(
      (
        tileMedia: TileMedia | null,
        tileMediaSpec: TileMediaSpec | undefined,
        getMediaTracking?: (
          tileMediaSpec: TileMediaSpec
        ) => TileMediaTracking | null
      ) => trackCompleted(false, tileMedia, tileMediaSpec, getMediaTracking),
      [trackCompleted]
    );

    const contextValue = React.useMemo(
      () => ({
        state: stateRef,
        setAvailableFilesReceivedPerformanceMs:
          isMediaBrowserPanelTrackingSupported
            ? // oxlint-disable-next-line react/refs
              once(setAvailableFilesReceivedPerformanceMs)
            : noOp,
        setDirectUrlReceivedPerformanceMs: isMediaBrowserPanelTrackingSupported
          ? // oxlint-disable-next-line react/refs
            once(setDirectUrlReceivedPerformanceMs)
          : noOp,
        setBlobFetchCompletePerformanceMs: isMediaBrowserPanelTrackingSupported
          ? // oxlint-disable-next-line react/refs
            once(setBlobFetchCompletePerformanceMs)
          : noOp,
        setDataCachedPerformanceMs: isMediaBrowserPanelTrackingSupported
          ? // oxlint-disable-next-line react/refs
            once(setDataCachedPerformanceMs)
          : noOp,
        setStatusObservedPerformanceMs: isMediaBrowserPanelTrackingSupported
          ? // oxlint-disable-next-line react/refs
            once(setStatusObservedPerformanceMs)
          : noOp,
        setMediaLoadedPerformanceMs: isMediaBrowserPanelTrackingSupported
          ? // oxlint-disable-next-line react/refs
            once(setMediaLoadedPerformanceMs)
          : noOp,
        setMediaLoadedSuccess: isMediaBrowserPanelTrackingSupported
          ? // oxlint-disable-next-line react/refs
            once(setMediaLoadedSuccess)
          : noOp,
        setTileMedia: isMediaBrowserPanelTrackingSupported
          ? // oxlint-disable-next-line react/refs
            once(setTileMedia)
          : noOp,
        trackSuccess: isMediaBrowserPanelTrackingSupported
          ? // oxlint-disable-next-line react/refs
            once(trackSuccess)
          : noOp,
        trackError: isMediaBrowserPanelTrackingSupported
          ? // oxlint-disable-next-line react/refs
            once(trackError)
          : noOp,
        emit,
        isMediaBrowserPanelTrackingSupported,
      }),
      [
        isMediaBrowserPanelTrackingSupported,
        setAvailableFilesReceivedPerformanceMs,
        setDirectUrlReceivedPerformanceMs,
        setBlobFetchCompletePerformanceMs,
        setDataCachedPerformanceMs,
        setStatusObservedPerformanceMs,
        setMediaLoadedPerformanceMs,
        setMediaLoadedSuccess,
        setTileMedia,
        trackSuccess,
        trackError,
        emit,
        noOp,
      ]
    );

    return (
      <MediaBrowserPanelTileTrackingContext.Provider value={contextValue}>
        {children}
      </MediaBrowserPanelTileTrackingContext.Provider>
    );
  }
);

export type ImageThumbnailSuccessMetadata = {
  // Whether this particular image has any thumbnails created for it
  hasThumbnails: boolean;
  // The size of the thumbnail that was loaded, if any
  usedThumbnailSize?: number;
};

export type VideoStreamingSuccessMetadata = {
  // Whether the entity has video streaming enabled
  isVideoStreamingEnabledForEntity: boolean;
  // Whether this particular video has been re-encoded for streaming
  wasVideoReencodedForStreaming: boolean;
  // Whether the video was streamed
  wasVideoStreamed: boolean;
  // The total length of the video in seconds
  totalVideoDuration: number;
  // The original video file size in bytes
  originalVideoFileSize: number;
  // The initial chunk size of the video in bytes if the video was re-encoded for streaming and was loaded successfully
  initialChunkSize?: number;
};

// Some metadata can only be tracked once the media is fully loaded, so it is passed into the trackSuccess callback.
type SuccessMetadata =
  | ImageThumbnailSuccessMetadata
  | VideoStreamingSuccessMetadata;

export function useGetTrackingCallbacks(
  tileMedia: TileMedia | null,
  tileMediaSpec: TileMediaSpec | undefined,
  getMediaTracking?: (tileMediaSpec: TileMediaSpec) => TileMediaTracking | null
) {
  const {trackSuccess, trackError} =
    useMediaBrowserPanelTileTrackingContextMaybe();
  const trackSuccessCallback = useCallback(
    (metadata?: SuccessMetadata) => {
      return trackSuccess(tileMedia, tileMediaSpec, getMediaTracking, metadata);
    },
    [trackSuccess, tileMedia, tileMediaSpec, getMediaTracking]
  );
  const trackErrorCallback = useCallback(() => {
    return trackError(tileMedia, tileMediaSpec, getMediaTracking);
  }, [trackError, tileMedia, tileMediaSpec, getMediaTracking]);
  return useMemo(
    () => ({
      trackSuccess: trackSuccessCallback,
      trackError: trackErrorCallback,
    }),
    [trackSuccessCallback, trackErrorCallback]
  );
}
